import { Assert } from "../../../Assert";
import { DataSource } from "../../../Data/DataSource";
import { EntityCommand, EntityCommandPropertyValues, EntityCommandTypeEnum, IEntityCommand } from "../../../EntityCommand";
import { EntityCommandInformation } from "../../../EntityCommandInformation";
import { Nullable, PartialNullable } from "../../../Extensions";
import { StringHelper } from "../../../Helpers/StringHelper";
import { Property } from "../../../RadObject";
import { PermissionEvaluator } from "../../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { DialogResultEnum } from "../../Dialogs/DialogResultEnum";
import { ButtonStyleEnum } from "../ButtonStyleEnum";
import { ComboBox } from "../ComboBox";
import { Dialog, DialogPropertyValues, IDialog } from "../Dialog";
import { EntitySearch } from "../EntitySearch";
import { EntitySearchSortDirectionEnum } from "../EntitySearchSortDirectionEnum";
import { PropertyEditorComboBox } from "../PropertyEditorComboBox";
import { NAMESPACE } from "./_Namespace";

export type SortOrderDialogPropertyValues =
    DialogPropertyValues
    & {
    };

export type SortOrderDialogProperties = {
    [name in keyof SortOrderDialogPropertyValues]: Property<SortOrderDialogPropertyValues[name]>
};

export interface ISortOrderDialog extends IDialog, SortOrderDialogProperties
{
}

let
    instance: SortOrderDialog | undefined;

export class SortOrderDialog extends Dialog implements ISortOrderDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntityCommandSort";
    public static override className = "SortOrderDialog";

    // #region Public Methods

    public static getSingleInstance(): SortOrderDialog
    {
        if (!instance)
            instance = new SortOrderDialog();

        return instance;
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<SortOrderDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okCancel,
                title: "Sort",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected entitySearch?: EntitySearch;
    protected propertyEditorColumn?: PropertyEditorComboBox;
    protected propertyEditorDirection?: PropertyEditorComboBox;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<SortOrderDialogPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _populatePropertyEditorColumn()
    {
        Assert.isNotNullOrUndefined(this.entitySearch, "this.entitySearch");
        Assert.isNotNullOrUndefined(this.propertyEditorColumn, "this.propertyEditorColumn");

        (this.propertyEditorColumn.editorControl.get() as Nullable<ComboBox>)?.listDataSource.set(
            new DataSource({
                data:
                    this.entitySearch.tableSearchResults?.columnsGet()
                        .filter(item =>
                        {
                            if (!(item as any).manual?.get)
                                return true;

                            return !(item as any).manual.get();
                        })
                        .map(item =>
                        {
                            return {
                                value: (item as any).dataSourceProperty?.get ? (item as any).dataSourceProperty.get() : null,
                                description: item.text.get()
                            };
                        })
                        .filter(item => item.value !== null)
            })
        );

        this.propertyEditorColumn.value.set(this.entitySearch.defaultSortListProperty.get());
    }
    protected _populatePropertyEditorDirection()
    {
        const
            directions = [];

        for (let property in EntitySearchSortDirectionEnum)
            directions.push(property);

        Assert.isNotNullOrUndefined(this.entitySearch, "this.entitySearch");
        Assert.isNotNullOrUndefined(this.propertyEditorDirection, "this.propertyEditorDirection");

        (this.propertyEditorDirection.editorControl.get() as Nullable<ComboBox>)?.listDataSource.set(
            new DataSource({
                data:
                    directions
                        .map(item =>
                        {
                            return {
                                value: item,
                                description: StringHelper.toUpperCamelCase(item)
                            };
                        })
            })
        );

        this.propertyEditorDirection.value.set(this.entitySearch.defaultSortListPropertyDirection.get());
    }
    protected _setEntitySearch(entitySearch: EntitySearch)
    {
        this.entitySearch = entitySearch;
        this._populatePropertyEditorColumn();
        this._populatePropertyEditorDirection();
    }

    // #endregion

    // #region Overrides

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.propertyEditorColumn =
            new PropertyEditorComboBox({
                editMode: true,
                editorControlProperties: {
                    allowEmptySelection: false
                },
                mandatory: true,
                propertyDisplayName: "Column"
            })
                .appendTo(this);

        this.propertyEditorDirection =
            new PropertyEditorComboBox({
                editMode: true,
                editorControlProperties: {
                    allowEmptySelection: false
                },
                mandatory: true,
                propertyDisplayName: "Direction"
            })
                .appendTo(this);

        return $$element;
    }

    public override async closeAsync()
    {
        if (this.dialogResult === DialogResultEnum.ok)
        {
            Assert.isNotNullOrUndefined(this.entitySearch, "this.entitySearch");
            Assert.isNotNullOrUndefined(this.propertyEditorColumn, "this.propertyEditorColumn");
            Assert.isNotNullOrUndefined(this.propertyEditorDirection, "this.propertyEditorDirection");

            this.entitySearch.defaultSortListProperty.set(this.propertyEditorColumn.value.get());
            this.entitySearch.defaultSortListPropertyDirection.set(this.propertyEditorDirection.value.get());
        }

        await super.closeAsync();
    }
    public override openAsync(entitySearch: EntitySearch)
    {
        this._setEntitySearch(entitySearch);
        return super.openAsync();
    }

    // #endregion

    // #endregion
}

export type EntityCommandSortPropertyValues =
    EntityCommandPropertyValues
    & {
    };

export type EntityCommandSortProperties = {
    [name in keyof EntityCommandSortPropertyValues]: Property<EntityCommandSortPropertyValues[name]>
};

export interface IEntityCommandSort extends IEntityCommand, EntityCommandSortProperties
{
}

export class EntityCommandSort extends EntityCommand<EntityCommandSortPropertyValues> implements IEntityCommandSort
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntityCommandSort";
    public static override className = "EntityCommandSort";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntityCommandSortPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                description: "Sort",
                key: "SORT",
                permission: PermissionEvaluator.permissionLevels.read,
                type: EntityCommandTypeEnum.entitySet,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Overrides

    protected override _executeAsync(entityCommandInformation: EntityCommandInformation, value: any)
    {
        return (
            super._executeAsync(entityCommandInformation, value)
                .then(() =>
                {

                    const
                        caller = entityCommandInformation.caller.get();

                    if (!(caller instanceof EntitySearch))
                        return;

                    return SortOrderDialog.getSingleInstance().openAsync(caller);
                })
        );
    }

    // #endregion

    // #endregion
}

